/* eslint-disable sort/object-properties */
import { LOANREQUEST_BUCKETS, USER_ROLES, USER_ROLES_WITHOUT_GUEST_AND_USER } from 'cpool-constants';
import { schemaEuropacePartnerId, schemaId, UserPhone } from 'cpool-schemas';
import { z } from 'zod';

import { GENDERS_ENUM } from '~/constants';

const emptyToNull = (arg: string | null | undefined) => {
    if (arg?.trim().length === 0) {
        return null;
    }
    return arg;
};

export const sMime = z.object({
    certFile: z.instanceof(File).optional(),
    certPassword: z.string().optional(),
    certFileName: z.string().nullish(),
});

/**
 * SMTP as it is edited in a form: an empty user/password pair means "not configured" and becomes
 * null, rather than failing the min(1) rules of the stored schema. Shared with the user settings
 * side modal, where a user without SMTP could otherwise never save.
 */
export const smtpForm = z
    .object({
        // null wird immer zu '' damit das mit der Form validierung richtig funktioniert
        user: z
            .string()
            .nullable()
            .default('')
            .transform((arg) => (arg === null ? '' : arg)),
        password: z
            .string()
            .nullable()
            .default('')
            .transform((arg) => (arg === null ? '' : arg)),
    })
    .nullable()
    .default(null)
    .transform((arg) => {
        // Wenn user und password leer sind, soll smtp NULL sein
        if (arg !== null && (arg.user ?? '').trim().length === 0 && (arg.password ?? '').trim().length === 0) {
            return null;
        }
        return arg;
    });

export const schemaEuropacePlatform = z.object({
    partnerId: z.union([schemaEuropacePartnerId, z.literal('')]).nullable(),
});

export const schemaUserEditForm = z
    .object({
        // Personal info
        gender: z.enum(GENDERS_ENUM),
        title: z.string().min(1),
        firstName: z.string().min(1),
        lastName: z.string().min(1),
        phone: z.preprocess((arg) => (arg === '' ? null : arg), UserPhone.nullish()),
        pid: z
            .string()
            .nullish()
            .transform((arg) => arg?.trim() || null),

        // Smtp
        smtp: smtpForm,

        // Technical integration
        role: z.nativeEnum(USER_ROLES_WITHOUT_GUEST_AND_USER),
        // group assignment, all optional: users may stay unassigned
        corporationId: schemaId.nullish(),
        divisionAggregateId: schemaId.nullish(),
        teamId: schemaId.nullish(),
        isNewTeam: z.boolean().optional(),
        ownedBuckets: z.array(z.nativeEnum(LOANREQUEST_BUCKETS)).optional(),
        procheckId: z
            .string()
            .nullish()
            .transform((arg) => ((arg ?? '').trim().length === 0 ? null : arg)),

        // Login
        crmaccess: z.string().optional(), // Wird momentan nicht benutzt
        email: z.string().email(),
        password: z.string().transform(emptyToNull),

        // Europace login
        europacePlatforms: z
            .object({
                europace2: schemaEuropacePlatform,
                starpool: schemaEuropacePlatform,
            })
            .nullable()
            .default(null)
            .transform((arg) => {
                if (arg === null) {
                    return { europace2: { partnerId: null }, starpool: { partnerId: null } };
                }
                const europace2PartnerId =
                    (arg.europace2.partnerId ?? '').trim().length === 0 ? null : arg.europace2.partnerId;
                const starpoolPartnerId =
                    (arg.starpool.partnerId ?? '').trim().length === 0 ? null : arg.starpool.partnerId;
                return {
                    europace2: { partnerId: europace2PartnerId },
                    starpool: { partnerId: starpoolPartnerId },
                };
            }),
    })
    .merge(sMime)
    .superRefine(({ divisionAggregateId, isNewTeam, role, teamId }, ctx) => {
        if (isNewTeam && !(role === USER_ROLES.team_leader && divisionAggregateId && teamId))
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'A new team needs a team leader and a division',
                path: ['teamId'],
            });
    });

export type SchemaUserEditForm = z.infer<typeof schemaUserEditForm>;
